import * as R from 'ramda';
import getFilters from '../../selectors/getSelectedFilters';
import { normalize, schema } from 'normalizr';
import { FILTER_REDESIGN } from '../../constants/config';
import getUseRightsholderAttribution from '../../utils/getUseRightsholderAttribution';

const generateId = b => {
    return b.broadcastName;
};

export const getBroadcastsQuery = (
    filters,
    client,
    currentBroadcasts,
    query = {},
) => {
    if (!client) return {};
    const hasRedesignedFilters = client.permissions?.includes(FILTER_REDESIGN);
    const overrides = currentBroadcasts?.filtersOverride || {};
    const globFilters = getFilters(filters);
    const useRightsholderAttribution = getUseRightsholderAttribution(
        client,
        client.id,
    );

    return R.omit(['accountOwnershipTypeIds'], {
        mediaUploadNames:
            query.mediaUploadNamesOverride || hasRedesignedFilters
                ? filters?.broadcastNames
                : query.mediaUploadNames,
        client: { id: client.id },
        orderBy: ['contentDate'],
        accountUsernames: globFilters.networks,
        ...(useRightsholderAttribution
            ? { useRightsholderAttribution: true }
            : {}),
        ...globFilters,
        ...overrides,
    });
};

const setBroadcasts =
    store =>
    (
        state,
        fetchedBroadcasts,
        isLoadingBroadcasts,
        query = {
            pageSize: 5,
            page: 0,
            mediaUploadNamesOverride: null,
        },
    ) => {
        const userClientId = state.user.selectedUserClientId;
        const filters = state.clients[userClientId].filters;

        // Sets filters as loading
        const loadingState = R.assocPath(
            ['clients', userClientId, 'broadcasts', 'isLoading'],
            isLoadingBroadcasts,
            state,
        );

        store.setState(loadingState);

        if (isLoadingBroadcasts) return;

        // Append rightsholder logo to the payload
        const rhsById = filters.rightsholders.list;

        const broadcastSchema = new schema.Entity('broadcastsById', undefined, {
            idAttribute: generateId,
            processStrategy(broadcast) {
                const brsps = (broadcast.broadcastRsps ?? []).map(brsp => {
                    const {
                        rightsholder: { id: rhId },
                        networks: ntks,
                    } = brsp;
                    const rh = rhsById[rhId];
                    const logoUrl = R.path(['mainLogo', 'url'], rh);

                    const networks = ntks
                        .sort((a, b) => {
                            if (a.name < b.name) {
                                return -1;
                            }
                            if (a.name > b.name) {
                                return 1;
                            }
                            return 0;
                        })
                        .reduce((acc, network, index, arr) => {
                            if (acc.length < 2) {
                                acc.push(network.name);
                            } else if (acc.length == 2) {
                                if (index == arr.length - 1) {
                                    acc.push(network.name);
                                } else {
                                    acc.push(
                                        `+${arr.length - 2} Other Networks`,
                                    );
                                }
                            }
                            return acc;
                        }, []);
                    return {
                        ...brsp,
                        networks,
                        rightsholder: {
                            ...brsp.rightsholder,
                            logoUrl,
                        },
                    };
                });

                return {
                    ...broadcast,
                    id: generateId(broadcast),
                    broadcastRsps: brsps,
                };
            },
        });

        const changesId = filters.changesId;
        const overridesId = filters.overridesId;

        const { result, entities } = normalize(fetchedBroadcasts, [
            broadcastSchema,
        ]);
        const lastState = store.getState();

        const currentById =
            lastState.clients[userClientId].broadcasts.byId || {};
        const currentOrder =
            lastState.clients[userClientId].broadcasts.order || [];
        const isFirstPage = !query.page || query.page === 0;
        const order = result || [];

        // Make sure that data saved is not duplicated due to duplicated network calls
        const broadcastsById = R.mergeRight(
            currentById,
            entities.broadcastsById,
        );
        const newOrder = isFirstPage
            ? order
            : R.uniq([...currentOrder, ...order]);

        store.setState(
            R.evolve(
                {
                    clients: {
                        [userClientId]: {
                            broadcasts: {
                                isLoading: R.F,
                                changesId: R.always(changesId),
                                overridesId: R.always(overridesId),
                                byId: R.always(broadcastsById),
                                order: R.always(newOrder),
                                hasMore: R.always(
                                    order.length ===
                                        parseInt(query.pageSize, 10),
                                ),
                                currentPage: R.always(query.page),
                            },
                        },
                    },
                },
                lastState,
            ),
            true,
        );
    };

export default setBroadcasts;
